/**
 * Gum Upsell (A)
 *
 * Usage:
 *   <gum-upsell-a data-min-quantity="2" data-money-format="...">...</gum-upsell-a>
 *
 * Public API:
 *   el.getCommittedSelection()
 *   el.hasCommittedSelection()
 *   el.openAddMode() / el.openEditMode()
 *
 * Events:
 *   gum-upsell-a:committed
 *   gum-upsell-a:removed
 */
(() => {
  const MODAL_OPEN_CLASS = 'is-open';
  const BODY_LOCK_CLASS = 'gum-upsell-a-modal-open';
  const SCROLL_UNLOCK_MS = 380;

  const toInt = (value, fallback = 0) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  /**
   * Shopify money formats (incl. Markets / non-USD) e.g.
   * "{{amount_no_decimals_with_space_separator}} som"
   */
  const formatMoney = (cents, moneyFormat = '${{amount}}') => {
    const formatString = String(moneyFormat || '${{amount}}');
    const rawCents = Number(cents);
    const safeCents = Number.isFinite(rawCents) ? rawCents : 0;

    const formatWithDelimiters = (numberCents, precision = 2, thousands = ',', decimal = '.') => {
      const value = (numberCents / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';
      return dollars + centsPart;
    };

    const placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch?.[1] || 'amount';
    let value = '';

    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(safeCents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(safeCents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(safeCents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(safeCents, 2, ' ');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(safeCents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(safeCents, 2, "'");
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(safeCents, 0, "'");
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(safeCents, 2);
        break;
    }

    return formatString.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  const measureScrollbarGap = () =>
    Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  class GumUpsellA extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.lastFocus = null;
      this.scrollUnlockTimer = null;
      this.isEditMode = false;
      this.editingPlan = null;
      this.committedByPlan = {
        subscription: null,
        onetime: null,
      };
      this.draftByPlan = {
        subscription: null,
        onetime: null,
      };
      this.editSnapshot = null;
      this.purchaseType = 'subscription';
      this.tiers = [];
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.cacheElements();
      this.readConfig();
      this.purchaseType = this.defaultPurchaseType;
      this.tiers = this.readTiers();

      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      this.syncUi();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.releaseScrollLock();
      this._connected = false;
    }

    cacheElements() {
      this.modal = this.querySelector('[data-gum-upsell-a-modal]');
      this.dialog = this.modal?.querySelector('.gum-upsell-a-modal__dialog') || null;
      this.flavorsEl = this.querySelector('[data-gum-upsell-a-flavors]');
      this.showAllBtn = this.querySelector('[data-gum-upsell-a-show-all]');
      this.tierEl = this.querySelector('[data-gum-upsell-a-tier]');
      this.submitBtn = this.querySelector('[data-gum-upsell-a-submit]');
      this.confirmationsEl = this.querySelector('[data-gum-upsell-a-confirmations]');
      this.confirmationListEl = this.querySelector('[data-gum-upsell-a-confirmation-list]');
      this.confirmationTemplate = this.querySelector('[data-gum-upsell-a-confirmation-template]');
      this.titleEl = this.querySelector('[data-gum-upsell-a-modal-title]');
      this.minTextEl = this.querySelector('[data-gum-upsell-a-min-text]');
      this.purchaseToggleEl = this.querySelector('[data-gum-upsell-a-purchase-toggle]');
    }

    readConfig() {
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.minQty = toInt(this.getAttribute('data-min-quantity'), 2);
      this.unitPriceCents = toInt(this.getAttribute('data-unit-price-cents'), 0);
      this.unitSaleCents = toInt(this.getAttribute('data-unit-sale-cents'), 0);
      this.defaultPurchaseType = this.getAttribute('data-default-purchase-type') || 'subscription';
    }

    readTiers() {
      const script = this.tierEl?.querySelector('[data-gum-upsell-a-tiers-json]');
      if (!script) return [];

      try {
        const parsed = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(parsed)) return [];

        return parsed
          .map((tier) => ({
            minQty: toInt(tier.minQty, 0),
            title: String(tier.title || ''),
            productId: tier.productId || null,
            variantId: tier.variantId || null,
            quantity: Math.max(1, toInt(tier.quantity, 1)),
            icon: tier.icon || null,
          }))
          .filter((tier) => tier.minQty > 0 && tier.title)
          .sort((a, b) => a.minQty - b.minQty);
      } catch (e) {
        return [];
      }
    }

    isSubscription() {
      return this.purchaseType === 'subscription';
    }

    getFlavorRows() {
      return Array.from(this.querySelectorAll('[data-gum-upsell-a-flavor]'));
    }

    getTotalQty() {
      return this.getFlavorRows().reduce((total, row) => {
        const input = row.querySelector('[data-gum-upsell-a-qty-input]');
        return total + toInt(input?.value, 0);
      }, 0);
    }

    getSelectionState() {
      const lines = [];
      let qty = 0;
      let onetime = 0;
      let sale = 0;

      this.getFlavorRows().forEach((row) => {
        const input = row.querySelector('[data-gum-upsell-a-qty-input]');
        const rowQty = toInt(input?.value, 0);
        if (rowQty <= 0) return;

        const onetimeCents = toInt(row.getAttribute('data-flavor-onetime-cents'), 0);
        const saleCents = toInt(row.getAttribute('data-flavor-sale-cents'), 0);

        qty += rowQty;
        onetime += onetimeCents * rowQty;
        sale += saleCents * rowQty;
        lines.push({
          id: row.getAttribute('data-flavor-id'),
          title: row.getAttribute('data-flavor-title') || '',
          qty: rowQty,
          onetimeCents,
          saleCents,
          sellingPlanId: row.getAttribute('data-flavor-selling-plan-id') || null,
        });
      });

      let unlockedTier = null;
      this.tiers.forEach((tier) => {
        if (qty >= tier.minQty) unlockedTier = tier;
      });

      return {
        purchaseType: this.purchaseType,
        lines,
        qty,
        onetimeCents: onetime,
        saleCents: this.isSubscription() ? sale : onetime,
        compareCents: onetime,
        saveCents: this.isSubscription() ? Math.max(0, onetime - sale) : 0,
        rewardTitle: this.isSubscription() && unlockedTier ? unlockedTier.title : '',
        rewardProductId: this.isSubscription() && unlockedTier ? unlockedTier.productId : null,
        rewardVariantId: this.isSubscription() && unlockedTier ? unlockedTier.variantId : null,
        rewardQuantity: this.isSubscription() && unlockedTier ? unlockedTier.quantity : 0,
      };
    }

    getTotals() {
      const state = this.getSelectionState();
      return {
        qty: state.qty,
        onetimeCents: state.onetimeCents,
        saleCents: state.saleCents,
        compareCents: state.compareCents,
        saveCents: state.saveCents,
      };
    }

    applyQtys(qtyMap = {}) {
      this.getFlavorRows().forEach((row) => {
        const input = row.querySelector('[data-gum-upsell-a-qty-input]');
        if (!input) return;
        const id = row.getAttribute('data-flavor-id');
        input.value = String(Math.max(0, toInt(qtyMap[id], 0)));
      });
    }

    resetQtys() {
      this.applyQtys({});
    }

    readQtyMap() {
      const map = {};
      this.getFlavorRows().forEach((row) => {
        const input = row.querySelector('[data-gum-upsell-a-qty-input]');
        const id = row.getAttribute('data-flavor-id');
        if (!id || !input) return;
        map[id] = toInt(input.value, 0);
      });
      return map;
    }

    applyModeUi() {
      if (this.titleEl) {
        const addTitle = this.titleEl.getAttribute('data-title-add') || 'Select your gum flavors';
        const editSub = this.titleEl.getAttribute('data-title-edit-subscription') || 'Edit your subscription gums';
        const editOtp = this.titleEl.getAttribute('data-title-edit-onetime') || 'Edit your one-time gums';
        this.titleEl.textContent = this.isEditMode
          ? this.isSubscription()
            ? editSub
            : editOtp
          : addTitle;
      }

      if (this.minTextEl) {
        const addMin = this.minTextEl.getAttribute('data-min-text-add') || 'Minimum 2 Boxes';
        const editMin = this.minTextEl.getAttribute('data-min-text-edit') || 'Plan locked · remove to change';
        this.minTextEl.textContent = this.isEditMode ? editMin : addMin;
      }

      if (this.purchaseToggleEl) {
        this.purchaseToggleEl.classList.toggle('is-locked', this.isEditMode);
        this.querySelectorAll('[data-gum-upsell-a-purchase]').forEach((btn) => {
          if (this.isEditMode) btn.setAttribute('aria-disabled', 'true');
          else btn.removeAttribute('aria-disabled');
        });
      }

      this.dialog?.setAttribute('data-mode', this.isEditMode ? 'edit' : 'add');
    }

    applyPurchaseTypeUi() {
      this.dialog?.setAttribute('data-purchase-type', this.purchaseType);

      this.querySelectorAll('[data-gum-upsell-a-purchase]').forEach((btn) => {
        const active = btn.getAttribute('data-gum-upsell-a-purchase') === this.purchaseType;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      const saleEl = this.querySelector('[data-gum-upsell-a-unit-sale]');
      const compareEl = this.querySelector('[data-gum-upsell-a-unit-compare]');

      if (saleEl) {
        saleEl.textContent = formatMoney(
          this.isSubscription() ? this.unitSaleCents : this.unitPriceCents,
          this.moneyFormat
        );
      }

      if (compareEl) {
        if (this.isSubscription() && this.unitPriceCents > this.unitSaleCents) {
          compareEl.textContent = formatMoney(this.unitPriceCents, this.moneyFormat);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }
    }

    updateFlavorRow(row) {
      const input = row.querySelector('[data-gum-upsell-a-qty-input]');
      const minusBtn = row.querySelector('[data-gum-upsell-a-qty-minus]');
      if (!input) return;
      const qty = Math.max(0, toInt(input.value, 0));
      input.value = String(qty);
      if (minusBtn) minusBtn.disabled = qty <= 0;
    }

    updateFooter() {
      const totals = this.getTotals();
      const submit = this.submitBtn;
      const countEl = this.querySelector('[data-gum-upsell-a-footer-count]');
      const totalEl = this.querySelector('[data-gum-upsell-a-footer-total]');
      const compareEl = this.querySelector('[data-gum-upsell-a-footer-compare]');
      const saveEl = this.querySelector('[data-gum-upsell-a-footer-save]');

      const boxesLabel = submit?.getAttribute('data-boxes-label') || 'Boxes';
      const boxLabel = submit?.getAttribute('data-box-label') || 'Box';
      const label = totals.qty === 1 ? boxLabel : boxesLabel;

      if (countEl) countEl.textContent = `${totals.qty} ${label}`;
      if (totalEl) totalEl.textContent = formatMoney(totals.saleCents, this.moneyFormat);

      if (compareEl && saveEl) {
        if (this.isSubscription() && totals.qty > 0 && totals.saveCents > 0) {
          compareEl.textContent = formatMoney(totals.compareCents, this.moneyFormat);
          compareEl.classList.remove('hidden');
          const savePrefix = (submit?.getAttribute('data-save-prefix') || 'Save').replace(/\s+$/, '');
          saveEl.textContent = `${savePrefix} ${formatMoney(totals.saveCents, this.moneyFormat)}`;
          saveEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
          saveEl.classList.add('hidden');
        }
      }

      if (!submit) return;

      const labelEl = submit.querySelector('[data-gum-upsell-a-submit-label]');
      const defaultLabel = submit.getAttribute('data-default-label') || 'Add to Order';
      const saveChangesLabel = submit.getAttribute('data-save-changes-label') || 'Save changes';
      const removeSelectionLabel =
        submit.getAttribute('data-remove-selection-label') || 'Remove selection';
      const addMoreTemplate = submit.getAttribute('data-add-more-label') || 'Add {n} more';
      const remaining = Math.max(0, this.minQty - totals.qty);
      let nextLabel = defaultLabel;
      let disabled = totals.qty < this.minQty;

      if (this.isEditMode) {
        if (totals.qty === 0) {
          nextLabel = removeSelectionLabel;
          disabled = false;
        } else if (remaining > 0) {
          nextLabel = addMoreTemplate.replace('{n}', String(remaining));
          disabled = true;
        } else {
          nextLabel = saveChangesLabel;
          disabled = false;
        }
      } else if (remaining > 0) {
        nextLabel = addMoreTemplate.replace('{n}', String(remaining));
        disabled = true;
      }

      if (labelEl) labelEl.textContent = nextLabel;
      submit.disabled = disabled;
      submit.setAttribute('data-action', this.isEditMode && totals.qty === 0 ? 'remove' : 'commit');
    }

    getOtherCommittedQty() {
      const otherPlan = this.purchaseType === 'subscription' ? 'onetime' : 'subscription';
      return Math.max(0, toInt(this.committedByPlan[otherPlan]?.qty, 0));
    }

    getCommittedPlans() {
      return ['subscription', 'onetime'].filter((plan) => {
        const state = this.committedByPlan[plan];
        return Boolean(state && state.qty >= this.minQty);
      });
    }

    updateTier() {
      if (!this.tierEl || !this.tiers.length) {
        this.tierEl?.classList.add('hidden');
        return;
      }

      const modalQty = this.getTotalQty();
      const qty = modalQty + this.getOtherCommittedQty();
      const textEl = this.tierEl.querySelector('[data-gum-upsell-a-tier-text]');
      const unlockedWrap = this.tierEl.querySelector('[data-gum-upsell-a-tier-unlocked]');
      const unlockedText = this.tierEl.querySelector('[data-gum-upsell-a-tier-unlocked-text]');
      const progressEl = this.tierEl.querySelector('[data-gum-upsell-a-tier-progress]');
      const iconWrap = this.tierEl.querySelector('[data-gum-upsell-a-tier-icon]');
      const unlockedSuffix = this.submitBtn?.getAttribute('data-unlocked-suffix') || 'added to your order';
      const maxPrefix = this.submitBtn?.getAttribute('data-max-reward-prefix') || 'Max Reward Unlocked';

      // Tracker only shows while the customer is picking boxes in the modal.
      if (modalQty <= 0) {
        this.tierEl.classList.add('hidden');
        return;
      }

      this.tierEl.classList.remove('hidden');

      let currentUnlocked = null;
      let nextTier = null;
      this.tiers.forEach((tier) => {
        if (qty >= tier.minQty) currentUnlocked = tier;
        else if (!nextTier) nextTier = tier;
      });

      const maxTier = this.tiers[this.tiers.length - 1];
      const firstTier = this.tiers[0];
      const progressTarget = nextTier || maxTier;
      const progressBase = currentUnlocked ? currentUnlocked.minQty : 0;
      const progressMax = progressTarget.minQty || 1;
      let progress = 0;

      if (!nextTier && currentUnlocked) {
        progress = 100;
      } else {
        progress = Math.max(
          0,
          Math.min(100, ((qty - progressBase) / Math.max(1, progressMax - progressBase)) * 100)
        );
        if (qty > 0 && progress < 8) progress = 8;
      }

      if (progressEl) progressEl.style.width = `${progress}%`;

      const existingImg = iconWrap?.querySelector('img');
      if (existingImg && progressTarget?.icon) existingImg.src = progressTarget.icon;

      const prefixEl = textEl?.querySelector('[data-gum-upsell-a-tier-prefix]');
      const suffixEl = textEl?.querySelector('[data-gum-upsell-a-tier-suffix]');

      const setTierMessage = (prefix, suffix, isMaxUnlocked) => {
        if (prefixEl) prefixEl.textContent = prefix;
        if (suffixEl) {
          suffixEl.textContent = suffix;
          suffixEl.classList.toggle('is-max-unlocked', !!isMaxUnlocked);
        }
      };

      if (!nextTier && currentUnlocked) {
        setTierMessage(maxPrefix, currentUnlocked.title, true);
        if (unlockedWrap && unlockedText) {
          unlockedWrap.classList.remove('hidden');
          unlockedText.textContent = `${currentUnlocked.title} ${unlockedSuffix}`;
        }
        return;
      }

      const need = nextTier ? Math.max(0, nextTier.minQty - qty) : 0;
      setTierMessage(`Add ${need} more`, nextTier ? nextTier.title : firstTier.title, false);

      if (unlockedWrap && unlockedText) {
        if (currentUnlocked) {
          unlockedWrap.classList.remove('hidden');
          unlockedText.textContent = `${currentUnlocked.title} ${unlockedSuffix}`;
        } else {
          unlockedWrap.classList.add('hidden');
        }
      }
    }

    saveDraftForCurrentPlan() {
      this.draftByPlan[this.purchaseType] = this.readQtyMap();
    }

    loadPlanQuantities(plan) {
      const draft = this.draftByPlan[plan];
      if (draft) {
        this.applyQtys(draft);
        return;
      }
      const committed = this.committedByPlan[plan];
      if (committed?.qtyMap) {
        this.applyQtys(committed.qtyMap);
        return;
      }
      this.resetQtys();
    }

    clearDrafts() {
      this.draftByPlan.subscription = null;
      this.draftByPlan.onetime = null;
    }

    fillConfirmationCard(card, state) {
      const submit = this.submitBtn;
      const boxesLabel = submit?.getAttribute('data-boxes-label') || 'Boxes';
      const boxLabel = submit?.getAttribute('data-box-label') || 'Box';
      const subLabel = submit?.getAttribute('data-subscription-plan-label') || 'Subscription';
      const otpLabel = submit?.getAttribute('data-onetime-plan-label') || 'One-time';
      const youSavePrefix = (submit?.getAttribute('data-you-save-prefix') || 'You Save').replace(/\s+$/, '');
      const planLabel = state.purchaseType === 'subscription' ? subLabel : otpLabel;
      const qtyLabel = state.qty === 1 ? boxLabel : boxesLabel;

      card.setAttribute('data-plan', state.purchaseType);

      const titleEl = card.querySelector('[data-gum-upsell-a-confirmation-title]');
      const flavorsEl = card.querySelector('[data-gum-upsell-a-confirmation-flavors]');
      const compareEl = card.querySelector('[data-gum-upsell-a-confirmation-compare]');
      const totalEl = card.querySelector('[data-gum-upsell-a-confirmation-total]');
      const saveEl = card.querySelector('[data-gum-upsell-a-confirmation-save]');
      const rewardEl = card.querySelector('[data-gum-upsell-a-confirmation-reward]');

      if (titleEl) titleEl.textContent = `${planLabel} • ${state.qty} ${qtyLabel}`;
      if (flavorsEl) {
        flavorsEl.textContent = state.lines.map((line) => `${line.qty}x ${line.title}`).join(' • ');
      }
      if (totalEl) totalEl.textContent = formatMoney(state.saleCents, this.moneyFormat);

      if (compareEl && saveEl) {
        if (state.purchaseType === 'subscription' && state.saveCents > 0) {
          compareEl.textContent = formatMoney(state.compareCents, this.moneyFormat);
          compareEl.classList.remove('hidden');
          saveEl.textContent = `${youSavePrefix} ${formatMoney(state.saveCents, this.moneyFormat)}`;
          saveEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
          saveEl.classList.add('hidden');
        }
      }

      if (rewardEl) {
        if (state.rewardTitle) {
          rewardEl.textContent = `+${state.rewardTitle}`;
          rewardEl.classList.remove('hidden');
        } else {
          rewardEl.textContent = '';
          rewardEl.classList.add('hidden');
        }
      }
    }

    renderConfirmations() {
      if (!this.confirmationsEl || !this.confirmationListEl || !this.confirmationTemplate) return;

      const plans = this.getCommittedPlans();
      this.confirmationListEl.innerHTML = '';

      if (!plans.length) {
        this.hideConfirmations();
        return;
      }

      // Subscription card above one-time when both exist.
      ['subscription', 'onetime'].forEach((plan) => {
        if (!plans.includes(plan)) return;
        const state = this.committedByPlan[plan];
        if (!state) return;
        const node = this.confirmationTemplate.content.cloneNode(true);
        const card = node.querySelector('[data-gum-upsell-a-confirmation-card]');
        if (!card) return;
        this.fillConfirmationCard(card, state);
        this.confirmationListEl.appendChild(node);
      });

      this.confirmationsEl.classList.remove('is-hiding', 'hidden');
      this.confirmationsEl.hidden = false;
    }

    /**
     * Hide confirmation cards (e.g. when Buy Now CTA starts flushing gums).
     * @param {{ animate?: boolean }} [options]
     */
    hideConfirmations(options = {}) {
      if (!this.confirmationsEl) return;

      const animate = options.animate !== false;
      const el = this.confirmationsEl;

      const finish = () => {
        el.classList.add('hidden');
        el.hidden = true;
        el.classList.remove('is-hiding');
        if (this.confirmationListEl) this.confirmationListEl.innerHTML = '';
        if (this._confirmationsHideTimer) {
          clearTimeout(this._confirmationsHideTimer);
          this._confirmationsHideTimer = null;
        }
      };

      if (
        !animate ||
        el.hidden ||
        el.classList.contains('hidden') ||
        el.classList.contains('is-hiding')
      ) {
        if (el.classList.contains('is-hiding')) return;
        finish();
        return;
      }

      if (this._confirmationsHideOnEnd) {
        el.removeEventListener('transitionend', this._confirmationsHideOnEnd);
        this._confirmationsHideOnEnd = null;
      }
      if (this._confirmationsHideTimer) {
        clearTimeout(this._confirmationsHideTimer);
        this._confirmationsHideTimer = null;
      }

      this._confirmationsHideOnEnd = (event) => {
        if (event.target !== el) return;
        if (event.propertyName && event.propertyName !== 'opacity' && event.propertyName !== 'max-height') {
          return;
        }
        el.removeEventListener('transitionend', this._confirmationsHideOnEnd);
        this._confirmationsHideOnEnd = null;
        finish();
      };

      el.addEventListener('transitionend', this._confirmationsHideOnEnd);
      // Force style flush so the transition always runs from the open state.
      void el.offsetHeight;
      el.classList.add('is-hiding');

      this._confirmationsHideTimer = setTimeout(() => {
        if (this._confirmationsHideOnEnd) {
          el.removeEventListener('transitionend', this._confirmationsHideOnEnd);
          this._confirmationsHideOnEnd = null;
        }
        finish();
      }, 380);
    }

    notifyTotalsChanged() {
      this.dispatchEvent(
        new CustomEvent('gum-upsell-a:totals-changed', {
          bubbles: true,
          detail: this.getCommittedTotalsCents(),
        })
      );
    }

    showToast(message) {
      const text = String(message || '').trim();
      if (!text) return;

      let toast = this._toastEl || document.querySelector('[data-gum-upsell-a-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'gum-upsell-a-toast';
        toast.setAttribute('data-gum-upsell-a-toast', '');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.innerHTML =
          '<span class="gum-upsell-a-toast__icon" aria-hidden="true">' +
          '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="9" cy="9" r="9" fill="#111111"/>' +
          '<path d="M5.2 9.1L7.8 11.6L12.8 6.4" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg></span>' +
          '<span class="gum-upsell-a-toast__text" data-gum-upsell-a-toast-text></span>';
        document.body.appendChild(toast);
      }
      this._toastEl = toast;

      const textEl = toast.querySelector('[data-gum-upsell-a-toast-text]');
      if (textEl) textEl.textContent = text;

      toast.classList.remove('is-visible');
      // Force reflow so repeated toasts re-animate.
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
        this._toastTimer = null;
      }, 2800);
    }

    getToastMessage(type) {
      const map = {
        added: this.getAttribute('data-toast-added') || 'Gum selection added',
        updated: this.getAttribute('data-toast-updated') || 'Gum selection updated',
        removed: this.getAttribute('data-toast-removed') || 'Gum selection removed',
      };
      return map[type] || map.updated;
    }

    commitSelection() {
      const state = this.getSelectionState();
      if (state.qty < this.minQty) return false;

      const plan = this.isEditMode ? this.editingPlan || state.purchaseType : state.purchaseType;
      const existed = Boolean(this.committedByPlan[plan]);
      const wasEdit = this.isEditMode;

      this.committedByPlan[plan] = {
        ...state,
        purchaseType: plan,
        lines: state.lines.slice(),
        qtyMap: this.readQtyMap(),
      };
      this.draftByPlan[plan] = null;

      this.isEditMode = false;
      this.editingPlan = null;
      this.editSnapshot = null;
      this.renderConfirmations();
      this.close({ skipDiscard: true });
      this.notifyTotalsChanged();
      this.showToast(this.getToastMessage(wasEdit || existed ? 'updated' : 'added'));

      this.dispatchEvent(
        new CustomEvent('gum-upsell-a:committed', {
          bubbles: true,
          detail: this.getCommittedSelection(),
        })
      );

      return true;
    }

    removeCommitted(plan, options = {}) {
      if (!plan || !this.committedByPlan[plan]) return;

      this.committedByPlan[plan] = null;
      this.draftByPlan[plan] = null;
      this.isEditMode = false;
      this.editingPlan = null;
      this.editSnapshot = null;
      this.renderConfirmations();

      if (!this.hasCommittedSelection()) {
        this.clearDrafts();
        this.resetQtys();
        this.purchaseType = this.defaultPurchaseType;
      }

      this.applyModeUi();
      this.syncUi();
      this.notifyTotalsChanged();
      if (!options.silent) this.showToast(this.getToastMessage('removed'));

      this.dispatchEvent(
        new CustomEvent('gum-upsell-a:removed', {
          bubbles: true,
          detail: {
            blockId: this.getAttribute('data-block-id'),
            plan,
          },
        })
      );
    }

    openAddMode() {
      this.isEditMode = false;
      this.editingPlan = null;
      this.editSnapshot = null;

      // Prefill the last committed plan so customers can top up (brief behavior).
      if (this.committedByPlan.subscription) {
        this.purchaseType = 'subscription';
      } else if (this.committedByPlan.onetime) {
        this.purchaseType = 'onetime';
      } else {
        this.purchaseType = this.defaultPurchaseType;
      }

      this.loadPlanQuantities(this.purchaseType);
      this.applyModeUi();
      this.open();
    }

    openEditMode(plan) {
      const state = this.committedByPlan[plan];
      if (!state) return;

      this.isEditMode = true;
      this.editingPlan = plan;
      this.purchaseType = plan;
      this.applyQtys(state.qtyMap || {});
      this.editSnapshot = {
        purchaseType: plan,
        qtyMap: { ...state.qtyMap },
      };
      this.applyModeUi();
      this.open();
    }

    switchPurchaseType(nextType) {
      if (this.isEditMode || !nextType || nextType === this.purchaseType) return;
      this.saveDraftForCurrentPlan();
      this.purchaseType = nextType;
      this.loadPlanQuantities(nextType);
      this.syncUi();
    }

    discardEditIfNeeded() {
      if (!this.isEditMode || !this.editSnapshot) {
        this.isEditMode = false;
        this.editingPlan = null;
        this.applyModeUi();
        return;
      }

      this.purchaseType = this.editSnapshot.purchaseType;
      this.applyQtys(this.editSnapshot.qtyMap);
      this.isEditMode = false;
      this.editingPlan = null;
      this.editSnapshot = null;
      this.applyModeUi();
      this.syncUi();
    }

    syncUi() {
      this.getFlavorRows().forEach((row) => this.updateFlavorRow(row));
      this.applyPurchaseTypeUi();
      this.applyModeUi();
      this.updateFooter();
      this.updateTier();
    }

    setCollapsed(collapsed) {
      if (!this.flavorsEl || !this.showAllBtn) return;
      this.flavorsEl.classList.toggle('is-collapsed', collapsed);
      this.showAllBtn.setAttribute('data-expanded', collapsed ? 'false' : 'true');

      const showAllLabel = this.showAllBtn.getAttribute('data-show-all-label') || 'Show All Flavors';
      const showFewerLabel = this.showAllBtn.getAttribute('data-show-fewer-label') || 'Show fewer flavors';
      const hiddenCount = this.showAllBtn.getAttribute('data-hidden-count') || '';
      const textEl = this.showAllBtn.querySelector('[data-gum-upsell-a-show-all-text]');
      if (!textEl) return;

      let countEl = textEl.querySelector('.gum-upsell-a-modal__show-all-count');
      if (!countEl) {
        countEl = document.createElement('span');
        countEl.className = 'gum-upsell-a-modal__show-all-count';
        textEl.appendChild(countEl);
      }

      let labelNode = null;
      for (let i = 0; i < textEl.childNodes.length; i += 1) {
        if (textEl.childNodes[i].nodeType === 3) {
          labelNode = textEl.childNodes[i];
          break;
        }
      }
      if (!labelNode) {
        labelNode = document.createTextNode('');
        textEl.insertBefore(labelNode, countEl);
      }

      if (collapsed) {
        labelNode.textContent = `${showAllLabel} `;
        countEl.textContent = `+${hiddenCount}`;
        countEl.hidden = false;
      } else {
        labelNode.textContent = showFewerLabel;
        countEl.textContent = '';
        countEl.hidden = true;
      }
    }

    applyScrollLock() {
      if (this.scrollUnlockTimer != null) {
        clearTimeout(this.scrollUnlockTimer);
        this.scrollUnlockTimer = null;
      }
      document.documentElement.style.setProperty(
        '--gum-upsell-a-scrollbar-gap',
        `${measureScrollbarGap()}px`
      );
      document.body.classList.add(BODY_LOCK_CLASS);
    }

    releaseScrollLock() {
      document.body.classList.remove(BODY_LOCK_CLASS);
      document.documentElement.style.removeProperty('--gum-upsell-a-scrollbar-gap');
    }

    scheduleScrollUnlock() {
      if (this.scrollUnlockTimer != null) {
        clearTimeout(this.scrollUnlockTimer);
        this.scrollUnlockTimer = null;
      }

      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (reduced) {
        this.releaseScrollLock();
        return;
      }

      this.scrollUnlockTimer = setTimeout(() => {
        this.scrollUnlockTimer = null;
        this.releaseScrollLock();
      }, SCROLL_UNLOCK_MS);
    }

    open() {
      if (!this.modal) return;
      this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.modal.classList.add(MODAL_OPEN_CLASS);
      this.modal.setAttribute('aria-hidden', 'false');
      this.applyScrollLock();
      this.syncUi();
      this.modal.querySelector('button[data-gum-upsell-a-close]')?.focus();
    }

    close(options = {}) {
      if (!this.modal) return;
      const wasOpen = this.modal.classList.contains(MODAL_OPEN_CLASS);
      this.modal.classList.remove(MODAL_OPEN_CLASS);
      this.modal.setAttribute('aria-hidden', 'true');

      if (wasOpen) this.scheduleScrollUnlock();
      else this.releaseScrollLock();

      if (options.skipDiscard) {
        this.isEditMode = false;
        this.editingPlan = null;
        this.editSnapshot = null;
        this.applyModeUi();
      } else {
        this.discardEditIfNeeded();
      }

      if (this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus();
      }
      this.lastFocus = null;
    }

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-gum-upsell-a-open]')) {
        this.openAddMode();
        return;
      }

      const editBtn = target.closest('[data-gum-upsell-a-edit]');
      if (editBtn) {
        const card = editBtn.closest('[data-gum-upsell-a-confirmation-card]');
        const plan = card?.getAttribute('data-plan');
        if (plan) this.openEditMode(plan);
        return;
      }

      const removeBtn = target.closest('[data-gum-upsell-a-remove]');
      if (removeBtn) {
        const card = removeBtn.closest('[data-gum-upsell-a-confirmation-card]');
        const plan = card?.getAttribute('data-plan');
        if (plan) this.removeCommitted(plan);
        return;
      }

      if (target.closest('[data-gum-upsell-a-close]')) {
        this.close();
        return;
      }

      const purchaseBtn = target.closest('[data-gum-upsell-a-purchase]');
      if (purchaseBtn) {
        if (this.isEditMode) return;
        const nextType = purchaseBtn.getAttribute('data-gum-upsell-a-purchase') || 'subscription';
        this.switchPurchaseType(nextType);
        return;
      }

      const minusBtn = target.closest('[data-gum-upsell-a-qty-minus]');
      if (minusBtn) {
        const row = minusBtn.closest('[data-gum-upsell-a-flavor]');
        const input = row?.querySelector('[data-gum-upsell-a-qty-input]');
        if (!input) return;
        input.value = String(Math.max(0, toInt(input.value, 0) - 1));
        this.syncUi();
        return;
      }

      const plusBtn = target.closest('[data-gum-upsell-a-qty-plus]');
      if (plusBtn) {
        const row = plusBtn.closest('[data-gum-upsell-a-flavor]');
        const input = row?.querySelector('[data-gum-upsell-a-qty-input]');
        if (!input) return;
        input.value = String(Math.max(0, toInt(input.value, 0) + 1));
        this.syncUi();
        return;
      }

      if (target.closest('[data-gum-upsell-a-show-all]')) {
        const expanded = this.showAllBtn?.getAttribute('data-expanded') === 'true';
        this.setCollapsed(expanded);
        return;
      }

      if (target.closest('[data-gum-upsell-a-submit]')) {
        if (this.submitBtn?.disabled) return;
        if (this.submitBtn?.getAttribute('data-action') === 'remove' && this.editingPlan) {
          const plan = this.editingPlan;
          this.close({ skipDiscard: true });
          this.removeCommitted(plan);
          return;
        }
        this.commitSelection();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!this.modal?.classList.contains(MODAL_OPEN_CLASS)) return;
      this.close();
    }

    /** Public: committed sale/compare totals in cents (up to one sub + one otp). */
    getCommittedTotalsCents() {
      return this.getCommittedPlans().reduce(
        (totals, plan) => {
          const set = this.committedByPlan[plan];
          if (!set) return totals;
          totals.saleCents += Math.max(0, toInt(set.saleCents, 0));
          totals.compareCents += Math.max(0, toInt(set.compareCents, 0));
          return totals;
        },
        { saleCents: 0, compareCents: 0 }
      );
    }

    /** Public: committed selection(s) for ATC / parent integrations. */
    getCommittedSelection() {
      const plans = this.getCommittedPlans();
      if (!plans.length) return null;

      const sets = plans.map((plan) => {
        const state = this.committedByPlan[plan];
        return {
          purchaseType: state.purchaseType,
          lines: state.lines.slice(),
          qty: state.qty,
          onetimeCents: state.onetimeCents,
          saleCents: state.saleCents,
          compareCents: state.compareCents,
          saveCents: state.saveCents,
          rewardTitle: state.rewardTitle,
          rewardProductId: state.rewardProductId,
          rewardVariantId: state.rewardVariantId,
          rewardQuantity: state.rewardQuantity,
        };
      });

      const sub = sets.find((entry) => entry.purchaseType === 'subscription');
      const totals = this.getCommittedTotalsCents();

      return {
        plans: sets,
        qty: sets.reduce((sum, entry) => sum + entry.qty, 0),
        onetimeCents: sets.reduce((sum, entry) => sum + entry.onetimeCents, 0),
        saleCents: totals.saleCents,
        compareCents: totals.compareCents,
        saveCents: sets.reduce((sum, entry) => sum + entry.saveCents, 0),
        rewardTitle: sub?.rewardTitle || '',
        rewardProductId: sub?.rewardProductId || null,
        rewardVariantId: sub?.rewardVariantId || null,
        rewardQuantity: sub?.rewardQuantity || 0,
      };
    }

    hasCommittedSelection() {
      return this.getCommittedPlans().length > 0;
    }

    hasSubscriptionCommitted() {
      const sub = this.committedByPlan.subscription;
      return Boolean(sub && sub.qty >= this.minQty);
    }

    getSubscriptionDiscountCode() {
      return String(this.getAttribute('data-subscription-discount-code') || '').trim();
    }

    /** Build Shopify /cart/add.js line items from committed plans. */
    buildCartItems() {
      const items = [];
      const lineProperties = {
        _sources: 'Toothpaste Upsell',
        _0013: 'variation A',
      };

      this.getCommittedPlans().forEach((plan) => {
        const selection = this.committedByPlan[plan];
        if (!selection) return;

        const isSub = plan === 'subscription';
        selection.lines.forEach((line) => {
          if (!line.id || line.qty <= 0) return;
          const item = {
            id: Number(line.id),
            quantity: line.qty,
            properties: { ...lineProperties },
          };
          const planId = toInt(line.sellingPlanId, 0);
          if (isSub && planId > 0) item.selling_plan = planId;
          items.push(item);
        });
      });

      const sub = this.committedByPlan.subscription;
      const giftVariantId = toInt(sub?.rewardVariantId, 0);
      const giftQty = Math.max(0, toInt(sub?.rewardQuantity, 0));
      if (giftVariantId > 0 && giftQty > 0) {
        items.push({
          id: giftVariantId,
          quantity: giftQty,
          properties: { ...lineProperties },
        });
      }

      return items;
    }
  }

  const toCartJsEndpoint = (routePath) => {
    const path = String(routePath || '').split('?')[0];
    if (!path) return '/cart/add.js';
    return path.endsWith('.js') ? path : `${path.replace(/\/?$/, '')}.js`;
  };

  const getCartAddEndpoint = () => {
    const routeUrl = window.routes?.cart_add_url;
    if (typeof routeUrl === 'string' && routeUrl.length > 0) {
      return toCartJsEndpoint(routeUrl);
    }
    const shopifyRoot = window.Shopify?.routes?.root ?? '/';
    const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
    return `${normalizedRoot}cart/add.js`;
  };

  const getDiscountEndpoint = (code) => {
    const shopifyRoot = window.Shopify?.routes?.root ?? '/';
    const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
    return `${normalizedRoot}discount/${encodeURIComponent(code)}`;
  };

  const applyDiscountCode = async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return;
    try {
      await fetch(getDiscountEndpoint(trimmed), {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
    } catch (error) {
      console.warn('[GumUpsellA] Failed to apply discount code', trimmed, error);
    }
  };

  /**
   * Intercepts toothpaste ATC submit: add committed gums first, then resume Shrine submit.
   * Uses capture phase + one-shot retry (same pattern as clear-cart-before-atc).
   */
  class GumUpsellACartBridge {
    constructor() {
      this.isFlushing = false;
      this.isAttached = false;
      this.retryPassForms = new WeakSet();
    }

    attach() {
      if (this.isAttached) return;
      document.addEventListener('submit', this.onSubmit, true);
      this.isAttached = true;
    }

    findUpsell() {
      return document.querySelector('gum-upsell-a');
    }

    onSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

      const upsell = this.findUpsell();
      if (!(upsell instanceof GumUpsellA) || !upsell.hasCommittedSelection()) return;

      if (this.retryPassForms.has(form)) {
        this.retryPassForms.delete(form);
        return;
      }

      if (this.isFlushing) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const items = upsell.buildCartItems();
      if (!items.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      // Remove confirmation cards as soon as Buy Now starts.
      upsell.hideConfirmations();

      const submitter = event.submitter;
      const discountCode = upsell.hasSubscriptionCommitted()
        ? upsell.getSubscriptionDiscountCode()
        : '';
      void this.flushGumsThenResume(form, items, submitter, discountCode, upsell);
    };

    async flushGumsThenResume(form, items, submitter, discountCode = '', upsell = null) {
      this.isFlushing = true;

      const submitButtons = Array.from(
        form.querySelectorAll('button[type="submit"], button.product-form__submit, button[name="add"]')
      );
      const setLoading = (isLoading) => {
        submitButtons.forEach((btn) => {
          const spinner = btn.querySelector('.loading-overlay__spinner');
          if (isLoading) {
            btn.classList.add('loading');
            btn.setAttribute('aria-busy', 'true');
            // Don't disable — requestSubmit(submitter) fails if submitter is disabled.
            btn.style.pointerEvents = 'none';
            if (spinner) spinner.classList.remove('hidden');
          } else {
            btn.classList.remove('loading');
            btn.removeAttribute('aria-busy');
            btn.style.pointerEvents = '';
            if (spinner) spinner.classList.add('hidden');
          }
        });
      };

      const clearPointerEventsLock = () => {
        submitButtons.forEach((btn) => {
          btn.style.pointerEvents = '';
        });
      };

      setLoading(true);

      let succeeded = false;
      try {
        const addUrl = getCartAddEndpoint();
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          let message = `Could not add gum upsell (${response.status})`;
          try {
            const data = await response.json();
            if (data?.description) message = data.description;
            else if (data?.message) message = data.message;
          } catch (e) {
            /* ignore parse errors */
          }
          throw new Error(message);
        }

        if (discountCode) {
          await applyDiscountCode(discountCode);
        }

        this.retryPassForms.add(form);
        succeeded = true;

        // Tell clear-cart-before-atc.js's skip-cart+clear-cart handler (LP
        // "Buy Now" flow) not to clear the cart on this retry submit — we
        // just added the gum items above, and its own clear() would wipe
        // them before the main product gets added, leaving only the main
        // product at checkout.
        form.__nwSkipNextCartClear = true;

        // Unlock before resume / checkout redirect; keep spinner visible.
        clearPointerEventsLock();

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter instanceof HTMLElement ? submitter : undefined);
        } else {
          form.submit();
        }
      } catch (error) {
        console.error('[GumUpsellA] Failed to add gum items before toothpaste ATC', error);
        const errorWrap = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        const errorText = errorWrap?.querySelector('.product-form__error-message');
        if (errorWrap && errorText) {
          errorText.textContent = error?.message || 'Unable to add gum upsell. Please try again.';
          errorWrap.hidden = false;
        }
        // Restore confirmation cards if flush failed and shopper stays on PDP.
        if (upsell instanceof GumUpsellA) upsell.renderConfirmations();
      } finally {
        this.isFlushing = false;
        // Keep loader on success through toothpaste ATC → checkout.
        if (!succeeded) setLoading(false);
      }
    }
  }

  if (!customElements.get('gum-upsell-a')) {
    customElements.define('gum-upsell-a', GumUpsellA);
  }

  if (!window.__gumUpsellACartBridge) {
    window.__gumUpsellACartBridge = new GumUpsellACartBridge();
    window.__gumUpsellACartBridge.attach();
  }
})();

